<i18n>
{
  "en": {
    "product": "Products",
    "noProducts": "No products available"
  },
  "es": {
    "product": "Productos",
    "noProducts": "No hay productos disponibles"
  }
}
</i18n>

<template>
  <fragment>
    <v-progress-linear height="8" v-if="isProgressLoading" color="secondary" stream :buffer-value="masterProductProgress" :value="masterProductProgress"></v-progress-linear>
    <v-container>
      <div class="d-flex justify-center | header">
        <div class="justify-content-center font-weight-bold ma-1 text-md-h6 text-body-1"> {{ $t('product') }}</div>
      </div>
      <div class="mb-3 | filters">
        <master-product-filter :disabled="!startedFetching" @product="onProduct" @managed="onManaged" @new="onNew"/>
      </div>
      <div v-if="startedFetching">
        <v-virtual-scroll item-height="75" bench="20" :items="sortedFilteredProductList" class="scroll">
          <template v-slot:default="{ item }">
            <master-product-row :key="item.guid" :product="item" @edit="onEdit(item)"
              :isSubordinate="isSubordinate(item)"/>
          </template>
        </v-virtual-scroll>
      </div>
      <dynameat-skeleton v-if="isLoading" skeleton-type="menuList" :container="false"/>
      <master-product-modal :visible="productModalVisible" :product-prop="productToEdit"
                            @close="closeProductModal"/>
      <master-subordinate-product-modal :visible="subordinateProductModalVisible" :product-prop="productToEdit"
                                        @close="closeSubordinateModal"/>
    </v-container>
  </fragment>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import { normalizeStr } from '@core/helpers';
import MasterProductFilter from './components/MasterProductFilter.vue';
import MasterProductRow from './components/MasterProductRow.vue';
import MasterProductModal from './components/MasterProductModal.vue';
import MasterSubordinateProductModal from './components/MasterSubordinateProductModal.vue';

@Component
@Components({
  MasterProductRow,
  MasterProductFilter,
  MasterProductModal,
  MasterSubordinateProductModal,
  DynameatConfirmationModal,
  DynameatSkeleton
})
@MapActions(['masterProductListRequest', 'productMasterListRequest', 'setLoading', 'addNotification'])
@MapGetters(['isLoading', 'editableProductList', 'masterProductProgress'])
export default class MasterProductList extends Vue {
  productFilter = '';

  managedFilter = 'all';

  newFilter = 'all';

  productModalVisible = false;

  subordinateProductModalVisible = false;

  productToEdit = null;

  normalizedNamesMap = new Map();

  get sortedFilteredProductList() {
    return this.filteredProductList.sort((a, b) => b.is_new - a.is_new);
  }

  get filteredProductList() {
    let productList = this.editableProductList;
    if (this.managedFilter !== 'all') {
      productList = productList.filter((el) => el.is_managed === (this.managedFilter === 'yes'));
    }
    if (this.newFilter !== 'all') {
      productList = productList.filter((el) => el.is_new === (this.newFilter === 'yes'));
    }
    if (this.productFilter !== '' && this.normalizedNamesMap.size) {
      const token = normalizeStr(this.productFilter.trim());
      productList = productList
        .filter((el) => this.normalizedNamesMap.get(el.guid).normalizedName.includes(token)
            || this.normalizedNamesMap.get(el.guid).normalizedCommercialName.includes(token)
            || el.id_external.includes(token));
    }
    return productList;
  }

  get isProgressLoading() {
    return this.masterProductProgress < 100;
  }

  get startedFetching() {
    return this.editableProductList.length > 0;
  }

  onEdit(product) {
    this.productToEdit = product;
    if (this.isSubordinate(product)) this.subordinateProductModalVisible = true;
    else this.productModalVisible = true;
  }

  closeProductModal() {
    this.productToEdit = null;
    this.productModalVisible = false;
  }

  closeSubordinateModal() {
    this.productToEdit = null;
    this.subordinateProductModalVisible = false;
  }

  onProduct(string) {
    this.productFilter = string;
  }

  onNew(string) {
    this.newFilter = string;
  }

  onManaged(string) {
    this.managedFilter = string;
  }

  isSubordinate(product) {
    if (this.editableProductList) return this.editableProductList.some((el) => Object.values(el.sale_formats || {}).includes(product.guid));
    return false;
  }

  initializeNormalizedNamesMap() {
    this.editableProductList.forEach((el) => {
      this.normalizedNamesMap.set(el.guid, {
        normalizedName: normalizeStr(el.name),
        normalizedCommercialName: normalizeStr(el.commercial_name)
      });
    });
  }

  async created() {
    await this.loadProductData();
    this.initializeNormalizedNamesMap();
  }

  async loadProductData() {
    await this.setLoading(1);
    await this.masterProductListRequest(this.$route.params.pointOfSaleGuid);
    await this.productMasterListRequest();
    await this.setLoading(-1);
  }

  @Watch('$route.params.pointOfSaleGuid')
  async onPosGuidChanged(newVal, oldVal) {
    if (newVal !== oldVal) {
      await this.loadProductData();
    }
  }

  @Watch('editableProductList')
  async onProductListChange() {
    this.initializeNormalizedNamesMap();
  }
}

</script>
<style scoped>
.header {
  height: 32px;
}
.filters {
  height: 140px;
}
.scroll {
  max-height: calc(85vh - 140px - 32px)
}
</style>
